import { type ReactElement, useState } from "react";
import { useCallback, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { type InclusionOption, binaryInclusionOptions } from "app/pages/sales-intelligence/types/commonMaps";

import type SiteTagsFilterModel from "../../../models/SiteTagsFilter/SiteTagsFilterModel";
import { SiteTagsFilterValue } from "../../../models/SiteTagsFilter/SiteTagsFilterValue";
import { FilterContentContainer, StyledHorizontalRadioSelect } from "../styles";

import ValuesDropdown from "./ValuesDropdown";

const INCLUSION_OPTIONS: readonly InclusionOption[] = binaryInclusionOptions;

const SiteTagsFilter = ({ value, onFilterUpdate }: IFilterViewBaseProps<SiteTagsFilterModel>): ReactElement => {
    const { translate } = useTranslate();

    const filterValue = useMemo(() => new SiteTagsFilterValue(value), [value]);

    const [inclusionType, setInclusionType] = useState<InclusionOption>(filterValue.inclusion);

    const handleValueChange = useCallback(
        (values: readonly string[]) => onFilterUpdate(filterValue.with({ values, inclusion: inclusionType })),
        [onFilterUpdate, filterValue, inclusionType],
    );

    const handleInclusionChange = (inclusion: InclusionOption) => {
        setInclusionType(inclusion);

        if (filterValue.isEmpty()) {
            return;
        }

        onFilterUpdate(filterValue.with({ inclusion }));
    };

    return (
        <FilterContentContainer>
            <StyledHorizontalRadioSelect
                items={INCLUSION_OPTIONS.map((id) => ({
                    id,
                    text: translate(`si.common.inclusions.${id}`),
                }))}
                size="sm"
                selected={inclusionType}
                onSelect={handleInclusionChange}
            />

            <ValuesDropdown
                values={filterValue.values}
                onChange={handleValueChange}
            />
        </FilterContentContainer>
    );
};

export default SiteTagsFilter;
